import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { StatusBadge } from "@/components/Badges";
import { STATUS_ORDER, VEHICLE_STATUSES, timeAgo, fmtTime } from "@/lib/constants";
import { Truck, History, X } from "lucide-react";
import { toast } from "sonner";

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/vehicles");
    setVehicles(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime((msg) => { if (msg.type === "vehicle_updated") load(); });

  const setStatus = async (vid, status) => {
    try {
      await api.post(`/vehicles/${vid}/status`, { status });
      toast.success("Status bijgewerkt");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openHistory = async (v) => {
    const { data } = await api.get(`/vehicles/${v.id}/history`);
    setHistory({ vehicle: v, items: data });
  };

  return (
    <div className="space-y-5" data-testid="vehicles-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Voertuigen</h1>
        <p className="text-sm text-slate-500">Handmatige statusbesturing van de vloot</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {vehicles.map((v) => (
          <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white"><Truck style={{ width: 20, height: 20 }} /></div>
                <div>
                  <div className="font-mono text-base font-bold text-slate-900">{v.callsign}</div>
                  <div className="text-sm text-slate-500">{v.name}</div>
                  <div className="text-xs text-slate-400">{v.type} · {v.home_base}</div>
                </div>
              </div>
              <button data-testid={`history-btn-${v.id}`} onClick={() => openHistory(v)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><History className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <StatusBadge status={v.status} />
              <span className="text-xs text-slate-400">sinds {timeAgo(v.status_since)}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {STATUS_ORDER.map((s) => (
                <button key={s} data-testid={`set-status-${v.id}-${s}`} onClick={() => setStatus(v.id, s)}
                  className={`rounded-xl border px-2 py-2 text-xs font-semibold transition-all ${v.status === s ? "" : "hover:opacity-80"}`}
                  style={{
                    backgroundColor: v.status === s ? VEHICLE_STATUSES[s].hex : VEHICLE_STATUSES[s].bg,
                    color: v.status === s ? "#fff" : VEHICLE_STATUSES[s].text,
                    borderColor: VEHICLE_STATUSES[s].border,
                  }}>
                  {VEHICLE_STATUSES[s].label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {history && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setHistory(null)}>
          <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="font-display font-bold text-slate-900">Statushistorie · {history.vehicle.callsign}</h3>
              <button onClick={() => setHistory(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-2 p-5">
              {history.items.length === 0 && <div className="text-sm text-slate-400">Geen historie</div>}
              {history.items.map((h) => (
                <div key={h.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    {h.from_status && <StatusBadge status={h.from_status} />}
                    <span className="text-slate-400">→</span>
                    <StatusBadge status={h.to_status} />
                  </div>
                  <div className="text-right text-xs text-slate-400"><div>{fmtTime(h.created_at)}</div><div>{h.changed_by}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
